import type { ReactElement } from 'react';
import { FilterController, FilterList } from '@amzn/stencil-react-components/filtering';

import { useMessages } from '../i18n/useMessages';
import {
  CUSTOM_DATE_RANGE_TYPE,
  buildDateRangeSelection,
  formatCustomRangeTitle,
  type FilterDateRange,
} from '../domain';
import { CustomDateRangeFilter } from './CustomDateRangeFilter';

const SHARED_BUNDLE = 'shared.ui.translations.dateRangeFilter';
type SharedKey =
  | 'dateRangePastWeek'
  | 'dateRangePastMonth'
  | 'dateRangePastYear'
  | 'dateRangeCustom';

type FilterChangeValue = string | number | (string | number)[] | undefined;

export interface UseDateRangeFilterElementParams {
  /**
   * Currently-applied date range. Drives the radio value and the chip title
   * fallback. `null` when no date range is applied.
   */
  filterDateRange: FilterDateRange | null;
  /** Parent-owned picker state — parent needs these at FilterBar.onApply commit time. */
  customStartDate: string | undefined;
  customEndDate: string | undefined;
  /** Called on radio change with the picked DateRangeType (or '' for a clear). */
  onTypeChange: (value: FilterChangeValue) => void;
  /**
   * Called when the picker's start/end changes, OR when a non-CUSTOM radio
   * is chosen and the stored dates should be dropped (both undefined).
   */
  onCustomDatesChange: (start: string | undefined, end: string | undefined) => void;
  /**
   * Called with a fully-built CUSTOM `FilterDateRange` when the picker changes,
   * so the parent can apply immediately without waiting for the flyout's
   * Apply button.
   */
  onApplyCustomRange: (range: FilterDateRange) => void;
  /** Feature-specific chip title (e.g. "Due Date", "Scheduled Date"). */
  titleText: string;
  /** Feature-specific text for the FilterList reset button. */
  resetButtonText: string;
  /** Feature-specific title shown inside the FilterList flyout panel. */
  listTitleText: string;
}

/**
 * Returns the date-range `FilterController` element for a list-page
 * FilterBar. Exposed as a hook (not a component) because Stencil's
 * FilterBar iterates its children with `React.Children.forEach` and
 * only recognises children whose element type is `FilterController`
 * itself — a wrapping component would be silently skipped and the
 * chip would never render.
 *
 * The caller invokes the hook and places the returned element directly
 * as a child of `<FilterBar>`:
 *
 * ```tsx
 * const dateRangeFilter = useDateRangeFilterElement({ ... });
 * return (
 *   <FilterBar>
 *     ...
 *     {dateRangeFilter}
 *   </FilterBar>
 * );
 * ```
 *
 * Owns the preset option list (Past Week / Past Month / Past Year /
 * Custom), the chip title derivation for a complete custom range, and
 * the coordination that clears stored custom dates when a non-CUSTOM
 * preset is picked — behavior that would otherwise be duplicated in
 * every filter bar.
 *
 * The parent owns `customStartDate` / `customEndDate` (so the FilterBar
 * onApply callback can read them when committing a CUSTOM range) and
 * receives edits via the callbacks.
 */
export function useDateRangeFilterElement({
  filterDateRange,
  customStartDate,
  customEndDate,
  onTypeChange,
  onCustomDatesChange,
  onApplyCustomRange,
  titleText,
  resetButtonText,
  listTitleText,
}: UseDateRangeFilterElementParams): ReactElement {
  const { t } = useMessages<SharedKey>(SHARED_BUNDLE);

  const dateRangeOptions = [
    { label: t('dateRangePastWeek'), value: 'PAST_WEEK' },
    { label: t('dateRangePastMonth'), value: 'PAST_MONTH' },
    { label: t('dateRangePastYear'), value: 'PAST_YEAR' },
    { label: t('dateRangeCustom'), value: CUSTOM_DATE_RANGE_TYPE },
  ];

  const isCustomSelected = filterDateRange?.type === CUSTOM_DATE_RANGE_TYPE;
  const customRangeTitle = isCustomSelected
    ? formatCustomRangeTitle(customStartDate, customEndDate)
    : null;

  return (
    <FilterController name="dateRange" titleText={customRangeTitle ?? titleText}>
      <FilterList
        onFilterChange={(value) => {
          onTypeChange(value);
          // Non-CUSTOM selection drops the stored custom dates so a later
          // re-selection of CUSTOM doesn't resurrect them. Flyout radios
          // never fire onFilterChange (see CustomDateRangeFilter's note),
          // so this path only fires from inline chip-level changes.
          if (String(value || '') !== CUSTOM_DATE_RANGE_TYPE) {
            onCustomDatesChange(undefined, undefined);
          }
        }}
        options={dateRangeOptions}
        resetButtonText={resetButtonText}
        titleText={listTitleText}
        value={filterDateRange?.type || undefined}
      />
      {/* Custom date range — reads the pending radio selection from
          FilterContext and shows itself when "Custom" is clicked, even
          before Apply (flyout radios don't fire onFilterChange). */}
      <CustomDateRangeFilter
        startDate={customStartDate}
        endDate={customEndDate}
        onRangeChange={(start, end) => {
          onCustomDatesChange(start, end);
          onApplyCustomRange(
            // Non-null: the picker always emits with the CUSTOM type in context.
            buildDateRangeSelection(
              CUSTOM_DATE_RANGE_TYPE,
              start,
              end,
            ) as FilterDateRange,
          );
        }}
      />
    </FilterController>
  );
}
